import { Formik, Form } from "formik";
import { useEffect, useState } from "react";
import * as yup from "yup";
import {
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Stack,
  TextField,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";

const filenameSchema = yup.object({
  fileName: yup
    .string()
    .required("File name is required")
    .matches(/^[a-zA-Z0-9._-]+$/, "Use letters, numbers, dots, underscores, or hyphens only"),
});

export type ExportImageDlgProps = {
  open: boolean;
  imageDataUrl?: string | null;
  defaultFileName?: string;
  klvMetadata?: Record<string, unknown> | null; // Snapshot of latest KLV at time of opening
  onCancel: () => void;
  onSave: (args: { 
    fileName: string; 
    addGeoMetadata: boolean; 
    format?: GeoMetadataOption; 
    metadata?: Record<string, unknown> | null;
    embedKlv?: boolean;
  }) => void;
};

const GEO_METADATA_STORAGE_KEY = "exportImage.geoMetadataFormat";
const GEO_METADATA_ENABLED_STORAGE_KEY = "exportImage.geoMetadataEnabled";
const EMBED_KLV_STORAGE_KEY = "exportImage.embedKlv";
const GEO_METADATA_OPTIONS = ["EXIF", "EXIF/XMP", "NITF"] as const;
type GeoMetadataOption = (typeof GEO_METADATA_OPTIONS)[number];

const ExportImageDlg = ({
  open,
  imageDataUrl,
  defaultFileName = "snapshot",
  klvMetadata,
  onCancel,
  onSave,
}: ExportImageDlgProps) => {
  const [addGeoMetadata, setAddGeoMetadata] = useState(false);
  const [geoMetadataFormat, setGeoMetadataFormat] = useState<GeoMetadataOption>("EXIF");
  const [embedKlv, setEmbedKlv] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const storedFormat = window.localStorage.getItem(
      GEO_METADATA_STORAGE_KEY,
    ) as GeoMetadataOption | null;
    if (storedFormat && GEO_METADATA_OPTIONS.includes(storedFormat)) {
      setGeoMetadataFormat(storedFormat);
    }
    const storedEnabled = window.localStorage.getItem(GEO_METADATA_ENABLED_STORAGE_KEY);
    if (storedEnabled === "true" || storedEnabled === "false") {
      setAddGeoMetadata(storedEnabled === "true");
    }
    const storedEmbedKlv = window.localStorage.getItem(EMBED_KLV_STORAGE_KEY);
    if (storedEmbedKlv === "true" || storedEmbedKlv === "false") {
      setEmbedKlv(storedEmbedKlv === "true");
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(GEO_METADATA_STORAGE_KEY, geoMetadataFormat);
  }, [geoMetadataFormat]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(GEO_METADATA_ENABLED_STORAGE_KEY, addGeoMetadata ? "true" : "false");
  }, [addGeoMetadata]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(EMBED_KLV_STORAGE_KEY, embedKlv ? "true" : "false");
  }, [embedKlv]);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <Formik
        initialValues={{ fileName: defaultFileName }}
        validationSchema={filenameSchema}
        enableReinitialize
        onSubmit={async (values, helpers) => {
          try {
            await Promise.resolve(
              onSave({
                fileName: values.fileName.trim(),
                addGeoMetadata,
                format: addGeoMetadata ? geoMetadataFormat : undefined,
                metadata: klvMetadata ?? null,
                embedKlv: geoMetadataFormat === "EXIF/XMP" ? embedKlv : undefined,
              }),
            );
          } finally {
            helpers.setSubmitting(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          isSubmitting,
          isValid,
          setFieldValue,
          setFieldTouched,
        }) => {
          // Determine file extension based on format
          const fileExtension = addGeoMetadata && geoMetadataFormat === "NITF" ? ".ntf" : ".jpg";
          
          return (
          <Form>
            <DialogTitle>Export snapshot</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                {isSubmitting && (
                  <Box sx={{ width: "100%" }}>
                    <LinearProgress />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                      {addGeoMetadata ? "Processing image with metadata..." : "Saving image..."}
                    </Typography>
                  </Box>
                )}
                <Card variant="outlined">
                  {imageDataUrl ? (
                    <CardMedia
                      component="img"
                      image={imageDataUrl}
                      alt="Snapshot preview"
                      sx={{ maxHeight: 360, objectFit: "contain", bgcolor: "black" }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 240,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "grey.900",
                        color: "grey.500",
                      }}
                    >
                      <Typography variant="body2">No preview available</Typography>
                    </Box>
                  )}
                  <CardContent>
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="File name"
                        name="fileName"
                        value={`${values.fileName}${fileExtension}`}
                        onBlur={(event) => {
                          const raw = event.target.value;
                          const trimmed = raw.trim().replace(/\.(jpg|ntf)$/i, "");
                          setFieldValue("fileName", trimmed);
                          setFieldTouched("fileName", true);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Backspace") {
                            const cursor = (event.target as HTMLInputElement).selectionStart ?? 0;
                            const suffixStart = (event.target as HTMLInputElement).value.length - fileExtension.length;
                            if (cursor > suffixStart) {
                              event.preventDefault();
                              return;
                            }
                          }
                        }}
                        onChange={(event) => {
                          let raw = event.target.value;
                          if (raw.toLowerCase().endsWith(fileExtension)) {
                            raw = raw.slice(0, -fileExtension.length);
                          }
                          setFieldValue("fileName", raw);
                        }}
                        autoFocus
                        error={Boolean(touched.fileName && errors.fileName)}
                        helperText={touched.fileName && errors.fileName ? errors.fileName : null}
                      />
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={addGeoMetadata}
                              onChange={(event) => setAddGeoMetadata(event.target.checked)}
                              size="small"
                            />
                          }
                          label="Add Geo Metadata"
                        />
                        {addGeoMetadata && (
                          <FormControl size="small" sx={{ minWidth: 160, maxWidth: 220 }}>
                            <InputLabel id="geo-metadata-format-label">Metadata format</InputLabel>
                            <Select
                              labelId="geo-metadata-format-label"
                              value={geoMetadataFormat}
                              label="Metadata format"
                              onChange={(event) =>
                                setGeoMetadataFormat(event.target.value as GeoMetadataOption)
                              }
                            >
                              {GEO_METADATA_OPTIONS.map((option) => (
                                <MenuItem key={option} value={option} disabled={option === "NITF"}>
                                  {option}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        {addGeoMetadata && geoMetadataFormat === "EXIF/XMP" && (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={embedKlv}
                                onChange={(event) => setEmbedKlv(event.target.checked)}
                                size="small"
                              />
                            }
                            label="Embed KLV"
                          />
                        )}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<CloseIcon />}
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                type="submit"
                disabled={!isValid || isSubmitting || !imageDataUrl}
              >
                Save
              </Button>
            </DialogActions>
          </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default ExportImageDlg;
